import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Check, Truck, Package, Home, Clock, RefreshCcw, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { formatJOD } from "../components/ProductCard";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { toast } from "sonner";
import { resolveImg } from "../lib/api";

export default function OrderTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const { t, tCity } = useLang();
  const { settings } = useSiteSettings();
  const isAuthed = !!(user && user !== false);

  const STEPS = [
    { key: "pending", label: t("status.pending"), icon: Clock },
    { key: "confirmed", label: t("status.confirmed"), icon: Check },
    { key: "shipped", label: t("status.shipped"), icon: Truck },
    { key: "delivered", label: t("status.delivered"), icon: Home },
  ];

  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch((e) => setError(e.response?.data?.detail || t("tracking.notFound")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (error)
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <h1 className="font-display font-black text-3xl">{error}</h1>
        <Link to="/account" className="mt-4 inline-block text-electric underline">{t("tracking.backToAccount")}</Link>
      </div>
    );

  if (!order) return <div className="py-24 text-center font-mono text-slate-500">{t("common.loading")}</div>;

  const cancelled = order.status === "cancelled";
  const currentIndex = cancelled ? -1 : STEPS.findIndex((s) => s.key === order.status);

  const reorder = async () => {
    const { data } = await api.post(`/orders/${order.id}/reorder`);
    if (!data.items.length) {
      toast.error(t("common.toast.reorderEmpty"));
      return;
    }
    data.items.forEach((it) =>
      addItem(
        { id: it.product_id, slug: it.slug, name: it.name, price: it.price, sale_price: null, images: [it.image] },
        it.quantity,
        it.variant_id ? { id: it.variant_id, label: it.variant_label, price: it.price, sale_price: null, stock: 999 } : null
      )
    );
    toast.success(`${data.items.length} ${t("common.toast.reorderAdded")}`);
    navigate("/cart");
  };

  return (
    <div data-testid="order-tracking-page" className="max-w-[1100px] mx-auto px-6 md:px-12 py-12">
      <Link to={isAuthed ? "/account" : "/"} className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-electric">
        {isAuthed ? t("tracking.backToOrders") : t("common.continueShopping")}
      </Link>
      <div className="mt-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">{t("tracking.orderEyebrow")}</div>
          <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
            #{order.id.slice(0, 8).toUpperCase()}
          </h1>
          <div className="mt-2 text-sm font-mono text-slate-500">
            {t("tracking.placedOn")} {new Date(order.created_at).toLocaleString()}
          </div>
        </div>
        {isAuthed && (
          <button
            onClick={reorder}
            data-testid="reorder-btn"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-ink text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-electric transition-colors"
          >
            <RefreshCcw size={14} /> {t("account.reorder")}
          </button>
        )}
        {order && (
          <a
            href={(() => {
              const phone = (settings?.general?.whatsapp_number || "962799448817").replace(/[^\d]/g, "");
              const shortId = order.id.slice(0, 8).toUpperCase();
              const lines = [
                `*${t("orderSuccess.waMsgHeader")}*`,
                `#${shortId}`,
                "",
                `*${t("orderSuccess.waItems")}:*`,
                ...(order.items || []).map((it) => {
                  const v = it.variant_label ? ` (${it.variant_label})` : "";
                  return `• ${it.name}${v} × ${it.quantity} — ${formatJOD(it.price * it.quantity)}`;
                }),
                "",
                `*${t("orderSuccess.waTotal")}:* ${formatJOD(order.total)}`,
              ];
              return `https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`;
            })()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tracking-whatsapp"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#25D366] text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-[#1ebe5d] transition-colors"
          >
            <MessageCircle size={14} /> {t("orderSuccess.waButton")}
          </a>
        )}
      </div>

      {/* Status timeline */}
      <div className="mt-10 bg-white rounded-2xl p-6 sm:p-10 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
        {cancelled ? (
          <div className="text-center py-6">
            <div className="inline-flex w-14 h-14 rounded-full bg-destructive/10 items-center justify-center mb-3">
              <Package size={22} className="text-destructive" />
            </div>
            <div className="font-display font-extrabold text-2xl">{t("tracking.cancelled")}</div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2 sm:gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i <= currentIndex;
              const active = i === currentIndex;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex flex-col items-center text-center flex-1 min-w-0">
                    <div
                      className={`w-12 h-12 rounded-full grid place-items-center transition-all duration-500 ${
                        done ? "bg-electric text-white shadow-[0_4px_20px_-4px_rgba(0,85,255,0.5)]" : "bg-secondary text-slate-400"
                      } ${active ? "scale-110 ring-4 ring-electric/15" : ""}`}
                    >
                      <Icon size={20} />
                    </div>
                    <div
                      className={`mt-3 text-xs font-mono uppercase tracking-widest ${
                        done ? "text-ink font-semibold" : "text-slate-400"
                      }`}
                    >
                      {s.label}
                    </div>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 h-0.5 mt-6 bg-secondary relative overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-electric transition-all duration-1000"
                        style={{ width: i < currentIndex ? "100%" : "0%" }}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">{t("tracking.itemsTitle")}</div>
          <div className="space-y-3">
            {order.items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-3 pb-3 border-b border-black/5 last:border-0 last:pb-0">
                {it.image && (
                  <Link to={`/product/${it.slug}`} className="w-14 h-14 bg-secondary rounded-lg overflow-hidden shrink-0">
                    <img src={resolveImg(it.image)} alt={it.name} className="w-full h-full object-contain p-1" />
                  </Link>
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${it.slug}`} className="font-display font-bold text-sm hover:text-electric truncate block">
                    {it.name}
                  </Link>
                  {it.variant_label && (
                    <div className="text-[10px] font-mono uppercase tracking-widest text-electric">{it.variant_label}</div>
                  )}
                  <div className="text-xs font-mono text-slate-500">
                    {formatJOD(it.price)} × {it.quantity}
                  </div>
                </div>
                <div className="font-mono font-semibold">{formatJOD(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">{t("tracking.summary")}</div>
          <div className="space-y-2 text-sm font-mono">
            <div className="flex justify-between"><span>{t("common.subtotal")}</span><span>{formatJOD(order.subtotal || order.total)}</span></div>
            {order.discount > 0 && (
              <div className="flex justify-between text-electric">
                <span>{t("common.discount")} {order.coupon_code ? `(${order.coupon_code})` : ""}</span>
                <span>− {formatJOD(order.discount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-black/10 font-display font-black text-lg">
              <span>{t("common.total")}</span><span>{formatJOD(order.total)}</span>
            </div>
          </div>
          <div className="mt-6 text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{t("tracking.shipping")}</div>
          <div className="text-sm font-mono leading-relaxed">
            {order.shipping?.full_name}<br />
            {order.shipping?.phone}<br />
            {order.shipping?.address}, {tCity(order.shipping?.city)}
          </div>
        </div>
      </div>
    </div>
  );
}
